/**
 * The front door (H1): what a stranger sees at jobcork.com.
 *
 * Benefit first. A job search normally eats your nights and weekends --
 * finding the right roles, tailoring a resume for each, filling the same
 * forms again and again. JobCork does that grunt work, so you apply to more
 * of the right jobs in far less time and stay in the running for interviews
 * you would never have had time to reach. The honesty that sets it apart from
 * spray-and-pray tools -- tailored, accurate, your call to submit -- is woven
 * in as a reason you win, not a promise we lead with. Customers assume we will
 * not lie; we sell what they get.
 *
 * Pinboard, like every other screen: the board on top, white cards pinned
 * below, one red.
 *
 * No fabricated metrics: no "3x more interviews" we cannot back yet -- the
 * honest claim is that using it beats doing nothing, so we make that one. No
 * pricing beyond what is true today: free for founder members while it is in
 * early access. No testimonials: there are none yet.
 */

import type { ReactNode } from "react";


/**
 * The board itself: a notice board with job postings pinned to it -- the
 * thing the name is short for. Drawn in the brand's own cool neutrals (the
 * board is the app's board colour, the cards its paper, the pins its one
 * red), so the picture and the product are the same object.
 */
export function BoardArt() {
  const pin = (x: number, y: number) => (
    <g key={`${x}-${y}`} transform={`translate(${x} ${y})`}>
      <path d="M0 6 L0 15" stroke="#14181f" strokeWidth="3" strokeLinecap="round" opacity="0.5" />
      <circle cx="0" cy="0" r="8" fill="#d9382f" />
      <circle cx="-3" cy="-3" r="2.4" fill="#ffffff" opacity="0.55" />
    </g>
  );
  const lines = (x: number, y: number, widths: number[], color = "#c9ced8") =>
    widths.map((w, i) => <rect key={i} x={x} y={y + i * 11} width={w} height="5" rx="2.5" fill={color} />);
  return (
    <svg className="board-art" viewBox="0 0 520 400" role="img" aria-label="A notice board with job postings pinned to it">
      <defs>
        <pattern id="cork" width="14" height="14" patternUnits="userSpaceOnUse">
          <circle cx="3" cy="4" r="1.1" fill="#3a4355" />
          <circle cx="10" cy="10" r="0.9" fill="#3a4355" />
          <circle cx="8" cy="2" r="0.6" fill="#454f63" />
        </pattern>
        <filter id="shadow" x="-10%" y="-10%" width="130%" height="140%">
          <feDropShadow dx="0" dy="6" stdDeviation="6" floodColor="#000000" floodOpacity="0.35" />
        </filter>
      </defs>
      <rect x="8" y="8" width="504" height="384" rx="14" fill="#2b3344" stroke="#3f4a5e" strokeWidth="6" />
      <rect x="20" y="20" width="480" height="360" rx="8" fill="url(#cork)" />
      <rect x="20" y="20" width="480" height="360" rx="8" fill="none" stroke="#1c2230" strokeWidth="2" opacity="0.6" />

      {/* A posting, with the receipt stamp */}
      <g transform="rotate(-3 150 150)" filter="url(#shadow)">
        <rect x="46" y="56" width="212" height="176" rx="6" fill="#ffffff" />
        <text x="62" y="86" fontFamily="Bricolage Grotesque, Arial Black, Arial, sans-serif" fontWeight="800" fontSize="17" fill="#14181f" letterSpacing="-0.4">Product Manager</text>
        <text x="62" y="106" fontFamily="Public Sans, Segoe UI, system-ui, sans-serif" fontSize="12" fill="#5e6573">Remote · $163k–$204k</text>
        {lines(62, 124, [176, 150, 164, 120])}
        <rect x="62" y="186" width="108" height="24" rx="4" fill="#e3f0e8" />
        <text x="72" y="202" fontFamily="Public Sans, Segoe UI, system-ui, sans-serif" fontWeight="600" fontSize="11" fill="#1f6b45" letterSpacing="0.6">SUBMITTED ✓</text>
      </g>
      {pin(152, 54)}

      {/* A second posting */}
      <g transform="rotate(2.5 380 130)" filter="url(#shadow)">
        <rect x="284" y="52" width="192" height="150" rx="6" fill="#ffffff" />
        <text x="300" y="82" fontFamily="Bricolage Grotesque, Arial Black, Arial, sans-serif" fontWeight="800" fontSize="17" fill="#14181f" letterSpacing="-0.4">Marketing Manager</text>
        <text x="300" y="102" fontFamily="Public Sans, Segoe UI, system-ui, sans-serif" fontSize="12" fill="#5e6573">Nashville, TN</text>
        {lines(300, 120, [150, 128, 142])}
        <rect x="300" y="166" width="62" height="20" rx="4" fill="#eceef2" />
        <text x="308" y="180" fontFamily="Public Sans, Segoe UI, system-ui, sans-serif" fontWeight="600" fontSize="10" fill="#3a404b" letterSpacing="0.6">NEW</text>
      </g>
      {pin(380, 50)}

      {/* The resume */}
      <g transform="rotate(-1.5 360 300)" filter="url(#shadow)">
        <rect x="290" y="224" width="176" height="150" rx="6" fill="#ffffff" />
        <rect x="306" y="240" width="88" height="7" rx="3.5" fill="#14181f" />
        <rect x="306" y="254" width="120" height="4" rx="2" fill="#aeb4bf" />
        {lines(306, 270, [138, 128, 136, 110, 132], "#d9dce2")}
        <text x="306" y="358" fontFamily="Public Sans, Segoe UI, system-ui, sans-serif" fontWeight="600" fontSize="10" fill="#5e6573" letterSpacing="0.6">RESUME · TAILORED</text>
      </g>
      {pin(378, 222)}

      {/* A note to self */}
      <g transform="rotate(4 150 310)" filter="url(#shadow)">
        <rect x="66" y="264" width="184" height="96" rx="6" fill="#f5f6f8" />
        <text x="82" y="296" fontFamily="Bricolage Grotesque, Arial Black, Arial, sans-serif" fontWeight="700" fontSize="15" fill="#14181f" letterSpacing="-0.3">Follow up Friday</text>
        <text x="82" y="316" fontFamily="Public Sans, Segoe UI, system-ui, sans-serif" fontSize="12" fill="#5e6573">Recruiter decides by</text>
        <text x="82" y="332" fontFamily="Public Sans, Segoe UI, system-ui, sans-serif" fontSize="12" fill="#5e6573">the end of the week.</text>
      </g>
      {pin(158, 262)}
    </svg>
  );
}

export function Landing({
  onSignup, onLogin, error, brand, signedIn = false,
}: {
  onSignup: () => void;
  onLogin: () => void;
  error: string | null;
  brand: ReactNode;
  /** Someone signed in, looking at the front door (#landing): the buttons take them back to their dashboard. */
  signedIn?: boolean;
}) {
  return (
    <>
      <div className="board">
        <main className="wrap wide hero">
          <div className="hero-copy">
            {brand}
            <h1>Job hunting, handled.</h1>
            <p className="lede">
              JobCork finds the roles that fit, tailors your resume and cover letter to each one, and
              fills the applications — so you apply to more of the right jobs in a fraction of the time.
              Watch it work in Chrome, send from your phone, or switch on Auto and let it apply while
              you're on shift.
            </p>
            <p className="lede">
              <strong>Every job you don't apply to is a shot you never took.</strong> JobCork keeps you
              applying — tailored, accurate, and in the running for the interview.
            </p>
            <div className="row">
              {signedIn ? (
                <button className="btn" onClick={onSignup}>Back to your dashboard</button>
              ) : (
                <>
                  <button className="btn" onClick={onSignup}>Become a founder member</button>
                  <button className="btn ghost" onClick={onLogin}>Sign in</button>
                </>
              )}
            </div>
            {error && <p className="err">{error}</p>}
            <p className="muted small">{signedIn ? "You are signed in; this is the page a stranger sees." : "Free for founder members while JobCork is in early access."}</p>
          </div>
          <div className="hero-art">
            <BoardArt />
            <p className="hero-caption">The board the name is short for: your postings, your resume, your notes — pinned where you can see them.</p>
          </div>
        </main>
      </div>

      <main className="wrap wide landing">
        <ol className="pinned">
          <li>
            <span className="step-n">Step 1</span>
            <h3>Upload your resume</h3>
            <p>We pull out the text and show it to you to fix. Every match and every application is built from it — the more accurate it is, the better JobCork works for you.</p>
          </li>
          <li>
            <span className="step-n">Step 2</span>
            <h3>Say what you're looking for</h3>
            <p>Titles, places, a pay floor. JobCork matches exactly that — real fits you'd actually take, not a padded list you have to sort through.</p>
          </li>
          <li>
            <span className="step-n">Step 3</span>
            <h3>It fills them in. You apply.</h3>
            <p>On Greenhouse, Lever, Ashby and Workable, JobCork fills the application from your resume and your confirmed answers. Watch it happen in Chrome and press Submit yourself, send a single role from your phone, or turn on Auto and let it apply to strong matches by your rules. Every one lands in your dashboard.</p>
          </li>
        </ol>

        {/*
          The benefits band. `.never` is just the brand's pin-dot bullet list
          (a red dot, a bold lead-in) -- reused here for what you GET. It used
          to hold "what it never does"; the page now leads with the win, not
          the disclaimer.
        */}
        <section className="landing-never">
          <h2>What you get</h2>
          <ul className="never">
            <li><strong>Only the jobs that fit.</strong> Matched to your titles, locations and pay floor — so you spend your time applying, not scrolling listings.</li>
            <li><strong>Tailored, never generic.</strong> A resume summary, a cover letter and answers written from your own resume for each role — so you read as a real candidate, not a mass-apply.</li>
            <li><strong>Apply your way.</strong> Watch it fill forms in Chrome, send a single role from your phone, or let Auto work your queue while you're on shift.</li>
            <li><strong>Always your call.</strong> You press Submit by default. Auto runs only by the rules you set, and stops the moment you turn it off.</li>
            <li><strong>Your data, yours to take.</strong> Download everything in one file, or delete your account and all of it, anytime.</li>
          </ul>
        </section>

        <section className="landing-split">
          <div>
            <h2>See exactly what you sent</h2>
            <p>
              JobCork keeps a clean record of every application — every field it filled, the answer, and
              where it came from. When a recruiter replies three weeks later, you'll know precisely what
              they read and how to follow up.
            </p>
            <p>
              Legally operative questions — work authorization, sponsorship, veteran and disability status —
              use only the answers you confirmed yourself. Where a form asks something in its own words,
              JobCork drafts from your resume and flags anything your resume doesn't back, so you approve it
              before it goes.
            </p>
          </div>
          <div className="receipt" aria-label="An example of the record JobCork keeps for one application">
            <p className="receipt-title">Senior Product Manager at Example Co</p>
            <p className="badge ok">Submitted · confirmed by the page</p>
            <ul className="audit">
              <li><span className="audit-when">9:41</span><span><span className="audit-what">Full name</span> — Jordan Reyes <span className="audit-value">— from your confirmed answers</span></span></li>
              <li><span className="audit-when">9:41</span><span><span className="audit-what">Resume</span> — the version you tailored for this role <span className="audit-value">— from your resume</span></span></li>
              <li><span className="audit-when">9:41</span><span><span className="audit-what">Work authorization</span> — answered <span className="audit-value">— from your confirmed answers</span></span></li>
              <li><span className="audit-when">9:42</span><span><span className="audit-what">Why this role?</span> — drafted <span className="audit-value">— written by the model; you read it</span></span></li>
              <li><span className="audit-when">9:42</span><span><span className="audit-what">Veteran status</span> — decline to self-identify <span className="audit-value">— you had not answered</span></span></li>
              <li className="audit-submitted"><span className="audit-when">9:43</span><span><span className="audit-what">Submitted</span> <span className="audit-value">— the page confirmed it</span></span></li>
            </ul>
          </div>
        </section>

        <section className="landing-auto">
          <h2>Auto, for when you can't check in</h2>
          <p>
            Some people can review each application. Some are on a twelve-hour shift or raising four kids and
            can't. Auto is for them: set the rules once — the least match score, how fresh a posting must be,
            a daily limit — and JobCork works your queue by those rules while your computer is off. Anything
            it can't answer from what you confirmed waits for you under <em>needs you</em>. Turn it off and it stops.
          </p>
        </section>

        <section className="landing-where">
          <h2>Where it works</h2>
          <p>
            JobCork fills applications on <strong>Greenhouse, Lever, Ashby and Workable</strong> — the
            application systems behind a huge share of the employers you'd apply to — and reads postings from
            those plus <strong>Workday</strong>, where the enterprise roles are. Boards whose terms forbid
            automation are left alone.
          </p>
        </section>

        <section className="landing-faq">
          <h2>Plainly</h2>
          <dl>
            <dt>Does it apply for me, or with me?</dt>
            <dd>Your choice. In Chrome, the extension fills the form, shows you what it filled, and stops — you press Submit. From any browser or your phone, <em>Send with JobCork</em> shows what will go and a browser in the cloud sends it. Hands-off, switch on Auto and it applies to matches above the score you set, only fresh postings, up to your daily limit. Anything it can't finish waits for you under <em>needs you</em>.</dd>
            <dt>Do I need Chrome?</dt>
            <dd>Only to watch it fill a form in front of you — that's the extension, and it runs in Chrome and browsers built on it. Sending from the cloud, and Auto, need no extension and work from Safari or a phone.</dd>
            <dt>What does it write?</dt>
            <dd>A tailored resume summary, a cover letter, and answers to an employer's free-text questions — all from your own resume and the posting, all yours to edit before anything is sent.</dd>
            <dt>What does it cost?</dt>
            <dd>Nothing for founder members while it's in early access. When that changes, you're told first.</dd>
            <dt>Can I leave?</dt>
            <dd>Any time. One button downloads everything JobCork holds about you; another deletes the account and all of it at once.</dd>
          </dl>
        </section>

        <section className="landing-cta">
          <h2>Get in the running.</h2>
          <div className="row">
            {signedIn ? (
              <button className="btn" onClick={onSignup}>Back to your dashboard</button>
            ) : (
              <>
                <button className="btn" onClick={onSignup}>Become a founder member</button>
                <button className="btn ghost" onClick={onLogin}>Sign in</button>
              </>
            )}
          </div>
        </section>
      </main>

      <footer className="site-foot">
        <div className="wrap wide">
          <span>© 2026 JobCork</span>
          <a href="/pricing.html">Pricing</a>
          <a href="/privacy.html">Privacy</a>
          <a href="/terms.html">Terms</a>
          <a href="mailto:hello@jobcork.com">hello@jobcork.com</a>
        </div>
      </footer>
    </>
  );
}
